import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, Search, Check } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { supportedLanguages } from "../data/translations";

export const LanguageSelector = () => {
  const { lang, setLang, t, meta } = useLanguage();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const filtered = supportedLanguages.filter((l) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return l.name.toLowerCase().includes(q) || l.native.toLowerCase().includes(q);
  });

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-3 py-2 rounded-full glass border border-[#D8DCE2]/10 hover:border-[#D7B56D]/40 transition-colors"
        data-testid="language-selector-btn"
        aria-label="Change language"
      >
        <Globe size={14} className="text-[#D7B56D]" />
        <span className="font-mono text-[11px] tracking-[0.18em] text-[#E6E0D6] uppercase">
          {meta.flag}
        </span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[100] flex items-start md:items-center justify-center p-4 md:p-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="language-modal"
          >
            <div
              className="absolute inset-0 bg-[#07090D]/85 backdrop-blur-md"
              onClick={() => setOpen(false)}
            />
            <motion.div
              initial={{ y: 30, opacity: 0, scale: 0.96 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 30, opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-full max-w-lg glass-strong rounded-3xl p-6 md:p-8 mt-12 md:mt-0"
            >
              <div className="flex items-center justify-between mb-5">
                <div>
                  <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#A9824A]">
                    {t("langTitle")}
                  </p>
                  <h3 className="font-display text-2xl font-bold text-[#F5F1EA] mt-1.5">
                    12 Languages · RTL Ready
                  </h3>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  className="text-[#D8DCE2] hover:text-[#D7B56D] text-2xl leading-none px-2"
                  data-testid="language-close-btn"
                  aria-label="Close"
                >
                  ×
                </button>
              </div>

              <div className="relative mb-5">
                <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A9824A]" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t("langSearch")}
                  className="w-full bg-[#1A1C22] border border-[#D8DCE2]/10 rounded-full py-3 pl-11 pr-4 text-[#F5F1EA] text-sm placeholder:text-[#D8DCE2]/40 focus:border-[#D7B56D]/40 focus:outline-none"
                  data-testid="language-search-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-2 max-h-[60vh] overflow-y-auto">
                {filtered.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => {
                      setLang(l.code);
                      setOpen(false);
                    }}
                    className={`flex items-center justify-between px-4 py-3 rounded-2xl text-start transition-all ${
                      lang === l.code
                        ? "bg-[#D7B56D]/10 border border-[#D7B56D]/40"
                        : "bg-[#1A1C22] border border-[#D8DCE2]/8 hover:border-[#D7B56D]/30"
                    }`}
                    data-testid={`language-option-${l.code}`}
                    dir={l.dir}
                  >
                    <div>
                      <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-[#A9824A] mb-1">
                        {l.flag}
                      </div>
                      <div className="text-sm text-[#F5F1EA] font-medium">{l.native}</div>
                    </div>
                    {lang === l.code && <Check size={16} className="text-[#D7B56D]" />}
                  </button>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
